import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  PipelineTemplate,
  PipelineNode,
  PipelineEdge,
  PipelineInstance,
  PipelineDiagram,
  CreateTemplateRequest,
  CreateNodeRequest,
  UpdateNodeRequest,
  CreateEdgeRequest,
} from '@/types/pipeline';
import { getAccessToken } from '@/utils/jwt';

export const pipelineApi = createApi({
  reducerPath: 'pipelineApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Pipeline', 'Instance'],
  endpoints: (builder) => ({
    listTemplates: builder.query<PipelineTemplate[], { active_only?: string }>({
      query: (params) => ({ url: '/pipeline/templates', params }),
      providesTags: ['Pipeline'],
    }),
    getTemplate: builder.query<PipelineTemplate, string>({
      query: (id) => `/pipeline/templates/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Pipeline', id }],
    }),
    createTemplate: builder.mutation<PipelineTemplate, CreateTemplateRequest>({
      query: (body) => ({ url: '/pipeline/templates', method: 'POST', body }),
      invalidatesTags: ['Pipeline'],
    }),
    updateTemplate: builder.mutation<
      PipelineTemplate,
      { id: string; body: Partial<PipelineTemplate> }
    >({
      query: ({ id, body }) => ({ url: `/pipeline/templates/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Pipeline', id }],
    }),
    deleteTemplate: builder.mutation<null, string>({
      query: (id) => ({ url: `/pipeline/templates/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Pipeline'],
    }),
    listNodes: builder.query<PipelineNode[], string>({
      query: (templateId) => `/pipeline/templates/${templateId}/nodes`,
      providesTags: ['Pipeline'],
    }),
    addNode: builder.mutation<PipelineNode, { templateId: string; body: CreateNodeRequest }>({
      query: ({ templateId, body }) => ({
        url: `/pipeline/templates/${templateId}/nodes`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Pipeline'],
    }),
    updateNode: builder.mutation<
      PipelineNode,
      { templateId: string; nodeId: string; body: UpdateNodeRequest }
    >({
      query: ({ templateId, nodeId, body }) => ({
        url: `/pipeline/templates/${templateId}/nodes/${nodeId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Pipeline'],
    }),
    deleteNode: builder.mutation<null, { templateId: string; nodeId: string }>({
      query: ({ templateId, nodeId }) => ({
        url: `/pipeline/templates/${templateId}/nodes/${nodeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Pipeline'],
    }),
    listEdges: builder.query<PipelineEdge[], string>({
      query: (templateId) => `/pipeline/templates/${templateId}/edges`,
      providesTags: ['Pipeline'],
    }),
    addEdge: builder.mutation<PipelineEdge, { templateId: string; body: CreateEdgeRequest }>({
      query: ({ templateId, body }) => ({
        url: `/pipeline/templates/${templateId}/edges`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Pipeline'],
    }),
    deleteEdge: builder.mutation<null, { templateId: string; edgeId: string }>({
      query: ({ templateId, edgeId }) => ({
        url: `/pipeline/templates/${templateId}/edges/${edgeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Pipeline'],
    }),
    getDiagram: builder.query<PipelineDiagram, string>({
      query: (templateId) => `/pipeline/templates/${templateId}/diagram`,
    }),
    validateTemplate: builder.query<{ valid: boolean; errors: string[] }, string>({
      query: (templateId) => `/pipeline/templates/${templateId}/validate`,
    }),
    listInstances: builder.query<PipelineInstance[], { template_id?: string }>({
      query: (params) => ({ url: '/pipeline/instances', params }),
      providesTags: ['Instance'],
    }),
    createInstance: builder.mutation<
      PipelineInstance,
      {
        template_id: string;
        entity_type: string;
        entity_id: string;
        extra_data?: Record<string, unknown>;
      }
    >({
      query: (body) => ({ url: '/pipeline/instances', method: 'POST', body }),
      invalidatesTags: ['Instance'],
    }),
    startInstance: builder.mutation<PipelineInstance, string>({
      query: (id) => ({ url: `/pipeline/instances/${id}/start`, method: 'POST' }),
      invalidatesTags: ['Instance'],
    }),
    pauseInstance: builder.mutation<PipelineInstance, string>({
      query: (id) => ({ url: `/pipeline/instances/${id}/pause`, method: 'POST' }),
      invalidatesTags: ['Instance'],
    }),
    completeInstance: builder.mutation<PipelineInstance, string>({
      query: (id) => ({ url: `/pipeline/instances/${id}/complete`, method: 'POST' }),
      invalidatesTags: ['Instance'],
    }),
    listCanonicalProjects: builder.query<
      { id: string; name: string; code: string; status: string }[],
      undefined
    >({
      query: () => '/canonical/projects',
      transformResponse: (response: {
        projects: { id: string; name: string; code: string; status: string }[];
      }) => response.projects,
    }),
    importFromShotGrid: builder.mutation<
      {
        template_id: string;
        template_name: string;
        steps_count: number;
        nodes_count: number;
        edges_count: number;
      },
      { project_id: string; template_name?: string }
    >({
      query: (body) => ({
        url: '/pipeline/templates/import/shotgrid',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Pipeline'],
    }),
  }),
});

export const {
  useListTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useListNodesQuery,
  useAddNodeMutation,
  useUpdateNodeMutation,
  useDeleteNodeMutation,
  useListEdgesQuery,
  useAddEdgeMutation,
  useDeleteEdgeMutation,
  useGetDiagramQuery,
  useValidateTemplateQuery,
  useListInstancesQuery,
  useCreateInstanceMutation,
  useStartInstanceMutation,
  usePauseInstanceMutation,
  useCompleteInstanceMutation,
  useListCanonicalProjectsQuery,
  useImportFromShotGridMutation,
} = pipelineApi;
